import type { FAQ } from './types';

/**
 * English mirror of platform.es.tsx.
 */
export const PLATFORM_FAQS_EN: FAQ[] = [
  {
    id: 'platform-add-carrier',
    category: 'platform',
    question: 'How do I add a new carrier (provider)?',
    tags: ['carrier', 'provider', 'twilio', 'telnyx'],
    cta: { label: 'Go to Providers', to: '/admin/providers' },
    answer: (
      <p>
        Admin → <strong>Providers</strong> → "Create". The name must match
        an adapter in the code (<code className="mono">twilio</code>,
        <code className="mono"> bandwidth</code>, etc). Providers without
        an adapter are persisted but the dispatcher skips them until the
        implementation is added.
      </p>
    ),
  },
  {
    id: 'platform-carrier-creds',
    category: 'platform',
    question: 'Where are carrier credentials stored?',
    tags: ['secrets', 'credentials', 'encrypted'],
    answer: (
      <p>
        In the <code className="mono">providers.secret_config_encrypted</code>{' '}
        column, encrypted with AES-256-GCM using the server's
        <code className="mono"> SECRETS_ENCRYPTION_KEY</code>. The server
        decrypts them at adapter load time; they're never plaintext in the
        DB. If you rotate the key, providers break until re-encrypted.
      </p>
    ),
  },
  {
    id: 'platform-carrier-health',
    category: 'platform',
    question: 'How does carrier health monitoring work?',
    tags: ['health', 'failover', 'monitor'],
    answer: (
      <p>
        The health-monitor worker pings each carrier every minute and
        updates <code className="mono">providers.health_status</code>{' '}
        (healthy / degraded / down). The dispatcher excludes "down" carriers
        from the cascade automatically — transparent failover for the send.
        Auto-disables a carrier after N consecutive failures.
      </p>
    ),
  },
  {
    id: 'platform-twilio-setup',
    category: 'platform',
    question: 'What env vars does Twilio need?',
    tags: ['twilio', 'env', 'config'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><code className="mono">TWILIO_ACCOUNT_SID</code> — starts with AC...</li>
        <li><code className="mono">TWILIO_AUTH_TOKEN</code> — your account secret</li>
        <li><code className="mono">TWILIO_FROM_NUMBER</code> — E.164 sender number</li>
        <li><code className="mono">TWILIO_BASE_URL</code> — optional, defaults to api.twilio.com</li>
        <li><code className="mono">TWILIO_STATUS_CALLBACK</code> — public URL of dlr-receiver</li>
      </ul>
    ),
  },
  {
    id: 'platform-dlr-receiver',
    category: 'platform',
    question: 'What does the dlr-receiver do?',
    tags: ['dlr', 'webhooks', 'callback'],
    answer: (
      <p>
        It's a separate binary listening on <code className="mono">/webhooks/{`{provider}`}/dlr</code>{' '}
        that receives carrier status callbacks (delivery confirmation,
        failure, etc). Without it running, messages stay in
        <code className="mono"> sent</code> forever — the carrier reports
        the <code className="mono">delivered</code> status but it never
        reaches the DB.
      </p>
    ),
  },
  {
    id: 'platform-memberships',
    category: 'platform',
    question: 'How do I give a user access to multiple workspaces?',
    tags: ['memberships', 'multi-tenant'],
    answer: (
      <p>
        On a customer detail page there's a "Workspace access" card where
        you can assign cross-tenant memberships. The user picks via the
        topbar switcher to jump between tenants. The JWT is reissued with
        the correct tenant_id on each switch (POST /auth/switch-tenant).
      </p>
    ),
  },
  {
    id: 'platform-audit-cross-tenant',
    category: 'platform',
    question: "Can a reseller's audit log see another reseller's actions?",
    tags: ['audit', 'isolation', 'tenant'],
    answer: (
      <p>
        No. audit_log is filtered by the actor's tenant_id — each reseller
        sees only their own mutations. Platform admin sees everything
        (super-tenant). If two resellers could see each other's log, that
        would be a leak.
      </p>
    ),
  },
  {
    id: 'platform-secrets-rotate',
    category: 'platform',
    question: 'How do I rotate credentials (Twilio, JWT, DB)?',
    tags: ['rotate', 'secrets', 'security'],
    answer: (
      <p>
        On the server, edit <code className="mono">/root/EzSender/infra/ovh-setup/config.env</code>{' '}
        with the new value, then run <code className="mono">.\deploy.ps1</code>{' '}
        which re-renders the runtime env and restarts services. Rotating
        JWT_SECRET → all users have to sign in again (old tokens become
        invalid). DB password → also run an ALTER USER in Postgres.
      </p>
    ),
  },
];
